// pages/Properties.tsx — the Developments list. Open one and everything inside it (buildings, units, record,
// contracts, parking…) is on its page. Table by default; the Filter card's "View" switches to cards (?view=grid,
// remembered per browser).
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useList } from '../hooks/useList';
import { DataTable, type Column } from '../components/ui/DataTable';
import { PageHeader, Badge, ActionMenu } from '../components/ui';
import { ListFilter } from '../components/ui/FilterMenu';
import { Confirm } from '../components/forms/common';
import { RequirePermission } from '../components/RequirePermission';
import { PropertyForm } from '../components/forms/PropertyForm';

type P = Record<string, any>;
type View = 'table' | 'grid';

/** Status is derived from the lifecycle stage on the server: LIVE, COMING_SOON or DISPOSED. */
const STATUS_TONE: Record<string, 'good' | 'warning' | 'neutral'> = { LIVE: 'good', COMING_SOON: 'warning', DISPOSED: 'neutral' };
const STAGES = ['LAND', 'PLANNING', 'CONSTRUCTION', 'LEASE_UP', 'STABILISED', 'DISPOSED'];
const title = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');
const VIEW_KEY = 'developments.view';
const readView = (): View => { try { return localStorage.getItem(VIEW_KEY) === 'grid' ? 'grid' : 'table'; } catch { return 'table'; } };

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'grid' ? 'grid' : params.get('view') === 'table' ? 'table' : readView();
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* private mode */ } }, [view]);
  const setView = (v: View) => {
    // Remember it first: without ?view= the page reads the remembered choice, so "Table" must be stored before the param goes.
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
    const next = new URLSearchParams(params); if (v === 'grid') next.set('view', 'grid'); else next.delete('view'); setParams(next, { replace: true });
  };

  const list = useList<P>('/properties');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<P | null>(null);
  const [removing, setRemoving] = useState<P | null>(null);
  const remove = useMutation({ mutationFn: async (p: P) => api.delete(`/properties/${p.id}`), onSuccess: () => { setRemoving(null); void list.refetch(); } });

  const actions = (p: P) => (
    <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
      <ActionMenu vertical label={`Actions for ${p.name}`} items={[
        { label: 'Edit development', icon: 'Pencil', can: 'properties.update', onClick: () => setEditing(p) },
        { label: 'Delete development', icon: 'Trash2', can: 'properties.delete', danger: true, separator: true, onClick: () => setRemoving(p) },
      ]} />
    </div>
  );

  const columns: Column<P>[] = [
    { key: 'name', label: 'Development name', sortKey: 'name', lockedVisible: true, render: (p) => <span className="font-medium text-ink-900">{p.name}</span> },
    { key: 'status', label: 'Status', sortKey: 'status', render: (p) => <Badge value={p.status} tone={STATUS_TONE[p.status] ?? 'neutral'} /> },
    // Residential / Commercial / Mixed / Industrial — the development's asset class.
    { key: 'assetClass', label: 'Type', sortKey: 'assetClass', render: (p) => p.assetClass ? title(p.assetClass) : '—' },
    { key: 'entityName', label: 'Legal Entity', sortKey: 'entityName', hideOnMobile: true, render: (p) => p.entityName ?? <span className="text-ink-300">—</span> },
    { key: 'city', label: 'Location', sortKey: 'city', render: (p) => p.city ?? '—' },
    { key: 'postcode', label: 'Postcode', sortKey: 'postcode', hideOnMobile: true, render: (p) => p.postcode ?? '—' },
    { key: 'unitCount', label: 'Units', sortKey: 'unitCount', align: 'right' },
    { key: 'storeys', label: 'No. of Storeys', sortKey: 'storeys', align: 'right', hideOnMobile: true, render: (p) => p.storeys ?? '—' },
    { key: 'apartmentTypes', label: 'Apartment Types', hideOnMobile: true, render: (p) => p.apartmentTypes ?? '—' },
    { key: 'lifecycleStage', label: 'Stage', sortKey: 'lifecycleStage', defaultHidden: true, render: (p) => <Badge value={p.lifecycleStage} tone={p.lifecycleStage === 'STABILISED' ? 'good' : p.lifecycleStage === 'CONSTRUCTION' ? 'warning' : 'neutral'} /> },
    { key: 'tenureModel', label: 'Model', defaultHidden: true, render: (p) => p.tenureModel === 'BTR' ? 'Build to rent' : 'Build to sell', hideOnMobile: true },
    { key: 'vacantUnitCount', label: 'Vacant', align: 'right', render: (p) => p.vacantUnitCount ?? '—' },
    { key: 'availableListingCount', label: 'Advertised', align: 'right', render: (p) => p.availableListingCount ?? '—', hideOnMobile: true },
    { key: 'actions', label: '', sortable: false, lockedVisible: true, width: '48px', render: actions },
  ];

  // The Filter card: View (one of two) first, then Status and Stage (tick several; sent comma-joined).
  const filterValue = { ...list.filters, view };
  const applyFilters = (next: Record<string, unknown>) => {
    const { view: v, ...rest } = next;
    setView(v === 'grid' ? 'grid' : 'table');
    list.setFilters(rest);
  };

  // Cards show the same fields the Columns button has ticked.
  const grid = (rows: P[], isShown: (key: string) => boolean) => {
    const stats = ([['unitCount', 'Units', (p: P) => p.unitCount], ['storeys', 'Storeys', (p: P) => p.storeys ?? '—'], ['vacantUnitCount', 'Vacant', (p: P) => p.vacantUnitCount ?? '—'], ['availableListingCount', 'Advertised', (p: P) => p.availableListingCount ?? '—']] as const).filter(([k]) => isShown(k));
    const where = (p: P) => [isShown('city') ? p.city : null, isShown('postcode') ? p.postcode : null].filter(Boolean).join(' · ');
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {rows.map((p) => (
          <div key={p.id} role="link" tabIndex={0} onClick={() => navigate(`/app/properties/${p.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/app/properties/${p.id}`); }}
            className="group cursor-pointer rounded-xl border border-ink-200 bg-white p-4 transition hover:border-bronze-300 hover:shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[14px] font-semibold text-ink-900">{p.name}</div>
                {(isShown('city') || isShown('postcode')) && <div className="truncate text-[12px] text-ink-500">{where(p) || '—'}</div>}
              </div>
              {actions(p)}
            </div>
            {(isShown('status') || isShown('assetClass') || isShown('lifecycleStage') || isShown('tenureModel')) && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {isShown('status') && <Badge value={p.status} tone={STATUS_TONE[p.status] ?? 'neutral'} />}
                {isShown('assetClass') && p.assetClass && <span className="text-[11.5px] text-ink-500">{title(p.assetClass)}</span>}
                {isShown('lifecycleStage') && <Badge value={p.lifecycleStage} tone={p.lifecycleStage === 'STABILISED' ? 'good' : p.lifecycleStage === 'CONSTRUCTION' ? 'warning' : 'neutral'} />}
                {isShown('tenureModel') && <span className="text-[11.5px] text-ink-500">{p.tenureModel === 'BTR' ? 'Build to rent' : 'Build to sell'}</span>}
              </div>
            )}
            {isShown('entityName') && <div className="mt-1.5 truncate text-[11.5px] text-ink-500" title={p.entityName ?? undefined}>{p.entityName ?? '\u00a0'}</div>}
            {stats.length > 0 && (
              <dl className={`mt-3 grid gap-2 border-t border-ink-100 pt-3 text-center ${['grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][stats.length - 1] ?? 'grid-cols-4'}`}>
                {stats.map(([k, l, v]) => (
                  <div key={k}><dt className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">{l}</dt><dd className="text-[14px] font-semibold tabular-nums text-ink-900">{v(p)}</dd></div>
                ))}
              </dl>
            )}
            {isShown('apartmentTypes') && p.apartmentTypes && <p className="mt-2.5 truncate text-[12px] text-ink-600" title={p.apartmentTypes}>{p.apartmentTypes}</p>}
          </div>
        ))}
      </div>
    );
  };

  return (
    <RequirePermission page="properties">
      <div>
        <PageHeader title="Developments" menu={[
          { label: 'New development', icon: 'Plus', can: 'properties.create', onClick: () => setCreating(true) },
          { label: 'Export CSV', export: { path: '/properties', page: 'properties', query: list.query } },
        ]} />
        <DataTable columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage} isLoading={list.isLoading} isFetching={list.isFetching} rowKey={(p) => p.id}
          onRowClick={(p) => navigate(`/app/properties/${p.id}`)} search={list.search} onSearch={list.setSearch} searchPlaceholder="Name, town, postcode or company…"
          sort={list.sort} onSort={(s) => { list.setPage(1); list.setSort(s); }} columnPicker={{ storageKey: 'developments.columns' }}
          renderGrid={view === 'grid' ? grid : undefined}
          emptyTitle="No developments yet" emptyBody="Add the first one, then its buildings and units on its page."
          filters={<ListFilter filters={filterValue} setFilters={applyFilters} groups={[
            { key: 'view', label: 'View', defaultValue: 'table', options: [{ value: 'table', label: 'Table' }, { value: 'grid', label: 'Grid' }] },
            { key: 'status', label: 'Status', multi: true, options: [{ value: 'LIVE', label: 'Live' }, { value: 'COMING_SOON', label: 'Coming soon' }, { value: 'DISPOSED', label: 'Disposed' }] },
            { key: 'lifecycleStage', label: 'Stage', multi: true, options: STAGES.map((s) => ({ value: s, label: title(s) })) },
          ]} />} />
        {creating && <PropertyForm open onClose={() => setCreating(false)} onSaved={(p) => navigate(`/app/properties/${p.id}`)} />}
        {editing && <PropertyForm open property={editing} onClose={() => { setEditing(null); void list.refetch(); }} />}
        <Confirm open={!!removing} onClose={() => setRemoving(null)} title={`Archive ${removing?.name ?? ''}?`}
          body={<>The development and its buildings are archived and disappear from every list. Nothing is destroyed and the audit trail keeps the record.{(removing?.unitCount ?? 0) > 0 && <span className="mt-2 block font-medium text-ink-800">It still has {removing!.unitCount} unit{removing!.unitCount === 1 ? '' : 's'}, so this will be refused until they are removed.</span>}</>}
          confirmLabel="Archive development" pending={remove.isPending} error={remove.error} onConfirm={() => remove.mutate(removing!)} />
      </div>
    </RequirePermission>
  );
}
